import { Badge, Paper, Text, Group, ThemeIcon, Stack } from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconQuestionMark,
  IconAlertTriangle,
} from '@tabler/icons-react';
import type { ScheduleGridCell } from '../../../../services/api/cohortCoaches';
import { SCHEDULE_CELL_CONFIG } from '../../../../config/statusConfig';

interface CoachScheduleCellProps {
  cell: ScheduleGridCell;
  sessionNumber: number;
  onSessionClick?: (sessionId: string) => void;
}

export function CoachScheduleCell({ cell, sessionNumber, onSessionClick }: CoachScheduleCellProps) {
  const config = SCHEDULE_CELL_CONFIG[cell.state];

  const handleClick = () => {
    if (cell.state === 'scheduled' && cell.session_id && onSessionClick) {
      onSessionClick(cell.session_id);
    }
  };

  const isClickable = cell.state === 'scheduled' && !!cell.session_id && !!onSessionClick;
  const hasSlotData =
    cell.available_slots != null && cell.total_slots != null && cell.total_slots > 0;
  const showSlotSummary =
    hasSlotData &&
    (cell.state === 'available' ||
      cell.state === 'available_conditions' ||
      cell.state === 'unavailable' ||
      cell.state === 'scheduled');

  const availableSlots = cell.available_slots ?? 0;
  const conflictSlots = cell.conflict_slots ?? 0;
  const totalSlots = cell.total_slots ?? 1;

  const hasWarning = hasSlotData && (conflictSlots > 0 || availableSlots < totalSlots);

  // For slot-summary states, use dashed orange border when conflicts exist
  const borderStyle = showSlotSummary
    ? hasWarning
      ? '1.5px dashed #E9A23B'
      : '1.5px dashed #00B894'
    : config.borderColor
      ? `1.5px ${config.borderStyle || 'solid'} ${config.borderColor}`
      : '1px solid #E8E6E1';

  return (
    <Paper
      p="xs"
      shadow="0"
      onClick={handleClick}
      style={{
        backgroundColor: showSlotSummary ? 'rgba(0,184,148,0.04)' : config.bgColor,
        border: borderStyle,
        borderRadius: 6,
        cursor: isClickable ? 'pointer' : 'default',
        minHeight: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: 'none',
      }}
    >
      {/* Slot summary rendering (scheduled/available/available_conditions/unavailable with slot data) */}
      {showSlotSummary && (
        <Stack gap={4} align="center" w="100%" px={4}>
          {(() => {
            const groupNames = cell.group_names && cell.group_names.length > 0
              ? cell.group_names
              : cell.group_name ? [cell.group_name] : [];
            if (groupNames.length === 0) return null;
            return (
              <Badge
                size="sm"
                variant="filled"
                style={{
                  backgroundColor: 'rgb(108, 92, 231)',
                  borderRadius: 999,
                }}
              >
                {groupNames.length === 1 ? groupNames[0] : `${groupNames.length} groups`}
              </Badge>
            );
          })()}
          <Group gap={4} justify="center" wrap="nowrap">
            <Text size="sm" fw={700} c="#1A1A1A">
              {availableSlots}
            </Text>
            <Text size="sm" c="#888888">
              /{totalSlots} open
            </Text>
            {hasWarning && <IconAlertTriangle size={16} color="#E9A23B" />}
          </Group>
        </Stack>
      )}

      {/* Scheduled without slot data (fallback) */}
      {cell.state === 'scheduled' && !showSlotSummary && (
        <Stack gap={2} align="center">
          <Text size="sm" fw={600} c="white" ta="center" lineClamp={1}>
            {cell.group_name}
          </Text>
          <Text size="xs" c="rgba(255,255,255,0.8)" ta="center">
            Session {sessionNumber}
          </Text>
        </Stack>
      )}

      {/* Available / Available with conditions without slot data (fallback) */}
      {(cell.state === 'available' || cell.state === 'available_conditions') && !showSlotSummary && (
        <Stack gap={2} align="center">
          <ThemeIcon size={22} radius="xl" color="teal" variant="light">
            <IconCheck size={14} />
          </ThemeIcon>
          <Text size="sm" c={config.color} fw={500} ta="center">
            Available
          </Text>
          {cell.remaining_capacity != null && cell.max_capacity != null && (
            <Text size="xs" c={config.color} ta="center">
              {cell.remaining_capacity}/{cell.max_capacity} groups
            </Text>
          )}
        </Stack>
      )}

      {/* Unavailable without slot data (fallback) */}
      {cell.state === 'unavailable' && !showSlotSummary && (
        <Stack gap={2} align="center">
          <ThemeIcon size={22} radius="xl" color="gray" variant="light">
            <IconX size={14} />
          </ThemeIcon>
          <Text size="sm" c={config.color} ta="center">
            Unavailable
          </Text>
        </Stack>
      )}

      {cell.state === 'awaiting_response' && (
        <Stack gap={2} align="center">
          <ThemeIcon size={22} radius="xl" color="orange" variant="light">
            <IconQuestionMark size={14} />
          </ThemeIcon>
          <Text size="sm" c={config.color} fw={500} ta="center">
            Awaiting
          </Text>
        </Stack>
      )}

      {cell.state === 'not_requested' && (
        <Text size="sm" c={config.color} ta="center">
          Not Requested
        </Text>
      )}
    </Paper>
  );
}
